/**
 * SearchHeaderMobile - Domain component for mobile search header
 * 
 * Component Layer: Domain (src/ui)
 * 
 * A simplified mobile header with embedded LocationSearch.
 * Based on ExpHeader layout but without:
 * - eXp logo
 * - Search button (replaced with embedded LocationSearch)
 * - International links button
 * 
 * Features:
 * - Fixed positioning at top
 * - Embedded location search bar
 * - Auth button (login or user menu)
 * - Menu dropdown button
 * 
 * @example
 * ```tsx
 * <SearchHeaderMobile
 *   tenantId="exp-realty-ca"
 *   locationQuery={query}
 *   setLocationQuery={setQuery}
 *   isLocationDropdownActive={isOpen}
 *   setIsLocationDropdownActive={setIsOpen}
 *   onLocationClick={handleLocationClick}
 * />
 * ```
 */

import React, { useState, useContext, useEffect } from 'react';
import { buildClassName } from 'utils/build-class-name';
import HeaderSearch from 'ui/header-search';
import MenuButton from 'components/header/menu-button';
import Dropdown from 'components/dropdown';
import { useUserContext } from 'contexts/user';
import { IModalContext, ModalContext } from 'contexts/modal';
import { useTranslatedHeaderConfig } from 'hooks/use-translated-header-config';
import { headerIds, testIds } from 'constants/test-constants';
import { TenantId } from '@tenants/types';
import useDebounce from 'hooks/use-debounce';
import styles from './search-header-mobile.module.scss';
import { CloseIcon, MenuIcon, UserIcon, BackIcon } from 'components/atomic/atoms/icons';

import type SearchPrediction from 'data/search-predictions';
import CircularIconButton from 'components/atomic/molecules/circular-icon-button';

//#region Types
export interface SearchHeaderMobileProps {
  /** Tenant ID for configuration */
  tenantId: TenantId;
  /** Current location search query */
  locationQuery: string;
  /** Whether the location dropdown is active */
  isLocationDropdownActive: boolean;
  /** Handler to update the search query */
  setLocationQuery: (query: string) => void;
  /** Handler to toggle dropdown visibility */
  setIsLocationDropdownActive: (isActive: boolean) => void;
  /** Handler called when a location prediction is clicked */
  onLocationClick: (searchPrediction: SearchPrediction) => void;
  /** Custom placeholder text for location search */
  placeholder?: string;
  /** Additional CSS class */
  className?: string;
  /** Handler for auth button click analytics */
  onAuthButtonClick?: () => void;
  /** Handler for menu button click analytics */
  onMenuButtonClick?: () => void;
  /** Handler for search bar click analytics */
  onSearchBarClickAnalytics?: () => void;
}
//#endregion

export function SearchHeaderMobile({
  tenantId,
  locationQuery,
  isLocationDropdownActive,
  setLocationQuery,
  setIsLocationDropdownActive,
  onLocationClick,
  placeholder,
  className,
  onAuthButtonClick,
  onMenuButtonClick,
  onSearchBarClickAnalytics,
}: SearchHeaderMobileProps) {
  // Auth and menu state
  const [menuDropdownIsActive, setMenuDropdownIsActive] = useState(false);
  const [userDropdownIsActive, setUserDropdownIsActive] = useState(false);
  
  // Scroll visibility state
  const [isVisible, setIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  
  // Scroll threshold - header will only hide/show after scrolling past this point
  const SCROLL_THRESHOLD = 100;
  
  // Contexts
  const { isAuthenticated, user } = useUserContext();
  const { openModal } = useContext(ModalContext) as IModalContext;
  
  // Translated header config
  const { authMenuItems, menuItems, authButtonCopy } = useTranslatedHeaderConfig(tenantId);

  // Toggle handlers
  const toggleMenuDropdown = () => {
    onMenuButtonClick?.();
    setMenuDropdownIsActive(prev => !prev);
  };

  const toggleUserDropdown = () => {
    onAuthButtonClick?.();
    setUserDropdownIsActive(prev => !prev);
  };

  const handleLoginClick = () => {
    onAuthButtonClick?.();
    openModal('login-registration');
  };

  // Generate auth user label (initials or first letter of email)
  const generateAuthUserLabel = () => {
    if (user) {
      if (user.firstName || user.lastName) {
        return `${(user?.firstName || '').slice(0, 1)}${(user?.lastName || '').slice(0, 1)}`;
      }
      return user?.email?.slice(0, 1);
    }
    return authButtonCopy;
  };
  const authUserLabel = generateAuthUserLabel();

  // Scroll handler for hide/show behavior
  const handleScroll = () => {
    const currentScrollY = window.scrollY;
    
    // Only apply hide/show logic if user has scrolled past the threshold
    if (currentScrollY > SCROLL_THRESHOLD) {
      if (currentScrollY > lastScrollY) {
        setIsVisible(false);
      } else {
        setIsVisible(true);
      }
    } else {
      // Keep header visible if scroll position is below threshold
      setIsVisible(true);
    }

    setLastScrollY(currentScrollY);
  };
  const debouncedScroll = useDebounce(handleScroll, 50);

  // Attach scroll listener
  useEffect(() => {
    window.addEventListener('scroll', debouncedScroll, { passive: true });
    return () => window.removeEventListener('scroll', debouncedScroll);
  }, [debouncedScroll]);

  return (
    <div className={buildClassName(styles.component, isVisible ? styles['is-visible'] : styles['is-hidden'], className)} data-testid={headerIds.header}>
      <div className={styles['layout-container']}>
        {/* Location Search - takes up main space */}
        <div className={styles['search-wrapper']}>
          {isLocationDropdownActive && (
            <CircularIconButton 
              icon={<BackIcon />}
              onClick={toggleMenuDropdown} 
              ariaLabel="Toggle menu"
              className={styles['back-icon']}
            />
          )}
          <HeaderSearch
            locationQuery={locationQuery}
            isLocationDropdownActive={isLocationDropdownActive}
            setLocationQuery={setLocationQuery}
            setIsLocationDropdownActive={setIsLocationDropdownActive}
            onClick={onLocationClick}
            placeholder={placeholder}
            className={styles['location-search']}
            onSearchBarClickAnalytics={onSearchBarClickAnalytics}
          />
        </div>

        {/* Navigation - auth and menu buttons */}
        {!isLocationDropdownActive && (
          <div className={styles.navigation}>
            {/* Auth Button - Login or User Menu */}
            <MenuButton 
              onClick={isAuthenticated ? toggleUserDropdown : handleLoginClick} 
              closeMenu={() => setUserDropdownIsActive(false)}
              className={styles.auth}
              testId={headerIds.loginHeader}
            >
              <>
                {isAuthenticated && (
                  <span data-testid={headerIds.loggedInUser}>
                    {authUserLabel}
                    {userDropdownIsActive && <Dropdown items={authMenuItems} className={styles['user-dropdown']} />}
                  </span>
                )}
                {!isAuthenticated && <UserIcon />}
              </>
            </MenuButton>

            {/* Menu Dropdown Button */}
            <MenuButton 
              className={buildClassName(styles.menu, menuDropdownIsActive && styles.active)} 
              testId={testIds.menuButton} 
              onClick={toggleMenuDropdown} 
              closeMenu={() => setMenuDropdownIsActive(false)}
            >
              <>
                {menuDropdownIsActive ? <CloseIcon /> : <MenuIcon />}
                {menuDropdownIsActive && <Dropdown items={menuItems} />}
              </>
            </MenuButton>
          </div>
        )}
      </div>
    </div>
  );
}

export default SearchHeaderMobile;
